import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3cfb8936"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label"]
const _hoisted_2 = ["tabindex", "src"]

import { reactive, ref, onMounted } from 'vue'
import type { Ref } from 'vue'
import { useEventBus } from '@vueuse/core'
import useLanguage from '@/composables/useLanguage'
import { gsap } from 'gsap'
import { VideoPlaybackData } from '../general/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoPlayer',
  setup(__props) {

const element = ref(null)
const videoPlayerElement = ref(null)
const videoPlayerOpenedBus = useEventBus('video-opened')
const videoPlayerClosedBus = useEventBus('video-closed')
const videoWrapper: Ref<HTMLElement | null> = ref(null)
const videoContainer: Ref<HTMLMediaElement | null> = ref(null)
const interfaceText = useLanguage('interface')
const state = reactive({
  filename: '',
  isOpen: false,
  isPlaying: false,
  transform: 'translate(0, 0) scale(0)',
  videoUID: '',
  width: 1920,
  height: 1080,
  aspectRatio: 16.0 / 9.0,
})
let videoPlayer
let lastActiveElement
const keyCodes = {
  ESC: 27,
  TAB: 9,
}

videoPlayerOpenedBus.on((info: unknown) => {
  const infoProxy = info as VideoPlaybackData
  if (videoContainer.value) {
    videoContainer.value.currentTime = 0
  }
  const translateX = -(window.innerWidth * 0.5 - infoProxy.x)
  const translateY = -(window.innerHeight * 0.5 - infoProxy.y)
  state.transform = `translate(${translateX}px, ${translateY}px) scale(0)`
  state.filename = infoProxy.filename
  state.videoUID = infoProxy.videoUID
  state.width = infoProxy.width
  state.height = infoProxy.height
  state.aspectRatio = infoProxy.width / infoProxy.height

  if (videoWrapper.value) {
    gsap.fromTo(
      videoWrapper.value,
      { transform: state.transform, opacity: 0 },
      {
        transform: 'translate(0, 0) scale(1)',
        opacity: 1,
        duration: 0.75,
        ease: 'power4.inOut',
      }
    )
  }
  if (videoPlayerElement.value) {
    const element = videoPlayerElement.value as HTMLIFrameElement
    setTimeout(() => {
      element.contentWindow?.focus()
    }, 1000)
  }
  state.isOpen = true
  trapFocus()
  if (videoPlayerElement.value) {
    const element = videoPlayerElement.value as HTMLIFrameElement
    console.log(element.contentWindow?.document)
    element.contentWindow?.document.addEventListener(
      'keydown',
      globalEscape,
      false
    )
  }
})

const globalEscape = (e) => {
  console.log('iframe keydown')
  var keyCode = e.keyCode || e.which
  switch (keyCode) {
    case keyCodes.ESC:
      e.preventDefault()
      closePlayer()
      break
    default:
      break
  }
}

const trapFocus = () => {
  lastActiveElement = document.activeElement
  document.addEventListener('keydown', handleKey)
}

const untrapFocus = () => {
  document.removeEventListener('keydown', handleKey)
  lastActiveElement.focus()
}

const handleKey = (e) => {
  var isTabPressed = e.key === 'Tab' || e.keyCode === keyCodes.TAB
  var isEscPressed = e.key === 'Escape' || e.keyCode === keyCodes.ESC

  console.log(isTabPressed, isEscPressed)

  if (isTabPressed) {
    if (videoPlayerElement.value) {
      const element = videoPlayerElement.value as HTMLIFrameElement
      element.contentWindow?.focus()
      e.preventDefault()
    }
  } else if (isEscPressed) {
    closePlayer()
    e.preventDefault()
  }
}

const getVideoPlayer = () => {
  if (!videoPlayer) {
    videoPlayer = window.Stream(videoPlayerElement.value)
  }
  return videoPlayer
}

const closePlayer = () => {
  getVideoPlayer().pause()
  state.isOpen = false
  if (videoWrapper.value) {
    gsap.to(videoWrapper.value, {
      transform: state.transform,
      opacity: 0,
      duration: 0.75,
      ease: 'power4.inOut',
      onComplete: () => {
        state.videoUID = ''
      },
    })
  }
  videoPlayerClosedBus.emit()
  untrapFocus()
}

const handlePlay = () => {
  state.isPlaying = true
}

const handlePause = () => {
  state.isPlaying = false
}

onMounted(() => {
  const script = document.createElement('script')
  script.setAttribute('id', 'cloudflare-stream-player')
  script.setAttribute('type', 'text/javascript')
  script.setAttribute(
    'src',
    'https://embed.cloudflarestream.com/embed/sdk.latest.js'
  )
  script.setAttribute('async', '')
  document.head.appendChild(script)
  script.onerror = () => {
    console.log('failed to load CloudFlare Stream Player')
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "element",
    ref: element,
    class: _normalizeClass(["absolute left-0 top-0 w-full h-full", {
      'pointer-events-none': !state.isOpen,
      'pointer-events-auto': state.isOpen,
    }])
  }, [
    _createElementVNode("button", {
      class: "absolute left-0 top-0 w-full h-full",
      "aria-label": _unref(interfaceText).close,
      onClick: closePlayer
    }, null, 8, _hoisted_1),
    _createElementVNode("div", {
      id: "videoWrapper",
      ref_key: "videoWrapper",
      ref: videoWrapper,
      class: "absolute w-full h-full left-0 top-0 flex items-center justify-center pointer-events-none scale-0"
    }, [
      _createElementVNode("div", {
        id: "videoContainer",
        ref_key: "videoContainer",
        ref: videoContainer,
        class: _normalizeClass(["relative flex items-center justify-center pointer-events-auto", {
          'w-[90%]': state.aspectRatio > 1,
          'h-[90%]': state.aspectRatio <= 1,
        }]),
        style: _normalizeStyle({
          aspectRatio: state.width + '/' + state.height,
        })
      }, [
        _createElementVNode("iframe", {
          id: "videoPlayer",
          ref_key: "videoPlayerElement",
          ref: videoPlayerElement,
          tabindex: state.isOpen ? 1 : -1,
          src: 
            state.videoUID
              ? `https://customer-vt66666ts449vzd9.cloudflarestream.com/${
                  state.videoUID[_unref(interfaceText).languageTag]
                }/iframe?preload=true&autoplay=true&controls=true&poster=https%3A%2F%2Fcustomer-vt66666ts449vzd9.cloudflarestream.com%2F${
                  state.videoUID[_unref(interfaceText).languageTag]
                }%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600`
              : ''
          ,
          allow: "accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;",
          class: _normalizeClass(["rounded-xl border-5px border-sprout box-content", {
            'w-full': state.aspectRatio > 1,
            'h-full': state.aspectRatio <= 1,
          }]),
          style: _normalizeStyle({
            aspectRatio: state.width + '/' + state.height,
          }),
          allowfullscreen: "true",
          onPlay: handlePlay,
          onPause: handlePause
        }, null, 46, _hoisted_2)
      ], 6)
    ], 512)
  ], 2))
}
}

})